<template>
  <div class="sset">
    <section class="sset__hero" data-tour="seller-settle-summary">
      <article class="sui-card sset__payout">
        <div class="sui-table-wrap">
          <table class="sui-table sset__sum">
            <caption>정산 현황</caption>
            <thead>
              <tr>
                <th>범위</th>
                <th>구분</th>
                <th class="is-num">건수</th>
                <th class="is-num">지급액</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="sset__sum-period" rowspan="3">전체</td>
                <th>정산 가능</th>
                <td class="is-num">{{ availableCount }}건</td>
                <td class="is-num">{{ formatWon(availableSum) }}원</td>
              </tr>
              <tr>
                <th>신청 중</th>
                <td class="is-num">{{ requestedCount }}묶음</td>
                <td class="is-num">{{ formatWon(requestedSum) }}원</td>
              </tr>
              <tr class="is-total">
                <th>완료</th>
                <td class="is-num">{{ paidCount }}묶음</td>
                <td class="is-num">{{ formatWon(paidSum) }}원</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="sset__meta">
          <span>
            입금 계좌
            <strong :class="{ 'is-warn': !hasAccount }">{{ payoutAccount }}</strong>
          </span>
        </p>
        <p v-if="!hasAccount" class="sset__cta">
          <router-link to="/seller/settings" class="sui-btn sui-btn--sm sui-btn--primary">
            정산 계좌 등록하기
          </router-link>
        </p>
      </article>

      <article class="sui-card sset__howto" data-tour="seller-settle-howto">
        <div class="sui-card__head">
          <h2 class="sui-card__title">정산 흐름</h2>
        </div>
        <div class="sui-card__body">
          <ol class="sset__steps">
            <li v-for="(step, index) in steps" :key="step.title">
              <em>{{ index + 1 }}</em>
              <div>
                <strong>{{ step.title }}</strong>
                <p>{{ step.desc }}</p>
              </div>
            </li>
          </ol>
        </div>
      </article>
    </section>

    <section class="sui-card sui-card--flush sset__board" data-tour="seller-settle-list">
      <div class="sset__sticky">
        <nav class="sset__filters" data-tour="seller-settle-filters" aria-label="정산 화면">
          <button
            type="button"
            class="sset__filter"
            :class="{ 'is-active': view === 'eligible', 'is-alert': availableCount > 0 }"
            @click="view = 'eligible'"
          >
            <span class="sset__filter-label">정산 가능</span>
          </button>
          <button
            type="button"
            class="sset__filter"
            :class="{ 'is-active': view === 'requests' }"
            @click="view = 'requests'"
          >
            <span class="sset__filter-label">정산신청내역</span>
          </button>
          <span class="sset__filters-spacer" />
          <span v-if="view === 'eligible'" class="sset__list-count">{{ eligible.length }}건</span>
          <span v-else class="sset__list-count">{{ requests.length }}묶음</span>
        </nav>

        <div class="sui-toolbar" data-tour="seller-settle-toolbar">
          <select
            v-if="view === 'eligible'"
            v-model="period"
            class="sui-select sset__period"
            aria-label="구매확정 연월"
            @change="reload"
          >
            <option value="">전체 기간 (구매확정일 기준)</option>
            <option v-for="option in periodOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
          <label class="sui-search">
            <AppIcon icon="mdi-magnify" :size="17" />
            <input
              v-model.trim="keyword"
              type="search"
              :placeholder="view === 'eligible' ? '주문번호 · 상품 검색' : '신청 이름 · 주문번호 · 상품'"
              @keyup.enter="reload"
            />
          </label>
          <button type="button" class="sui-btn" :disabled="loading" @click="reload">
            <AppIcon icon="mdi-magnify" :size="16" />
            검색
          </button>
        </div>

        <div v-if="view === 'eligible' && eligible.length" class="sset__bulk">
          <button type="button" class="sui-btn" :disabled="busy" @click="toggleAll(!allSelected)">
            {{ allSelected ? '선택 해제' : '검색 결과 전체 선택' }}
          </button>
          <strong>{{ selectedCount }}건 선택</strong>
          <span>지급액 {{ formatWon(selectedPayout) }}원</span>
          <span class="sset__bulk-spacer" />
          <button
            type="button"
            class="sui-btn sui-btn--primary"
            :disabled="busy || !selectedCount || !hasAccount"
            @click="onRequest"
          >
            {{ busy ? '신청 중…' : '정산 신청' }}
          </button>
        </div>

        <p v-if="successMessage" class="sset__done">{{ successMessage }}</p>
        <p v-if="errorMessage" class="sset__error">
          <AppIcon icon="mdi-alert-outline" :size="16" />
          {{ errorMessage }}
        </p>
      </div>

      <div class="sset__scroll">
        <div v-if="view === 'eligible' && eligible.length" class="sui-table-wrap">
          <table class="sui-table sset__table">
            <thead>
              <tr>
                <th class="sset__check">
                  <input
                    type="checkbox"
                    :checked="allSelected"
                    :indeterminate.prop="someSelected && !allSelected"
                    aria-label="검색 결과 전체 선택"
                    @change="toggleAll(($event.target as HTMLInputElement).checked)"
                  />
                </th>
                <th>주문번호</th>
                <th class="is-num">판매대금</th>
                <th class="is-num">함우리 수수료</th>
                <th class="is-num">판매업체 마진</th>
                <th class="is-num">배송비</th>
                <th class="is-num">지급액</th>
                <th>구매확정</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in eligible"
                :key="row.itemIdx"
                :class="{ 'is-checked': selected.has(row.itemIdx) }"
                @click="toggle(row.itemIdx)"
              >
                <td class="sset__check">
                  <input
                    type="checkbox"
                    :checked="selected.has(row.itemIdx)"
                    :aria-label="`${row.orderCode} 선택`"
                    @click.stop.prevent="toggle(row.itemIdx)"
                  />
                </td>
                <td>
                  <span class="sui-table__strong">{{ row.orderCode }}</span>
                  <span class="sui-table__sub">
                    {{ row.productName }}
                    <template v-if="row.optionLabel"> · {{ row.optionLabel }}</template>
                    · {{ row.qty }}{{ row.unit || '개' }}
                    <template v-if="row.lineKind === 'RETURN_FEE'"> · 단순 변심 반품비</template>
                    <template v-else-if="row.lineKind === 'SHIPPING'"> · 출고 배송비</template>
                  </span>
                </td>
                <td class="is-num">{{ formatWon(row.salesAmount) }}원</td>
                <td class="is-num sset__minus">-{{ formatWon(row.platformFee) }}원</td>
                <td class="is-num">{{ formatWon(row.sellerMargin) }}원</td>
                <td class="is-num">{{ formatWon(row.shippingFee || 0) }}원</td>
                <td class="is-num sui-table__strong">{{ formatWon(row.payout) }}원</td>
                <td>{{ row.lineKind === 'RETURN_FEE' ? '변심 반품비' : row.lineKind === 'SHIPPING' ? '출고 배송비' : formatDateTime(row.confirmedAt) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="view === 'requests' && requests.length" class="sui-table-wrap">
          <table class="sui-table sset__table">
            <thead>
              <tr>
                <th>요청</th>
                <th class="is-num">건수</th>
                <th class="is-num">지급액</th>
                <th>상태</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="request in requests" :key="request.id">
                <tr>
                  <td>
                    <button type="button" class="sset__bundle" @click="toggleBundle(request.id)">
                      <span class="sui-table__strong">{{ request.title }}</span>
                      <span class="sui-table__sub">{{ formatDateTime(request.requestedAt) }}</span>
                    </button>
                  </td>
                  <td class="is-num">{{ request.count }}건</td>
                  <td class="is-num sui-table__strong">{{ formatWon(request.payout) }}원</td>
                  <td>
                    <span v-if="request.kind === 'LEGACY'" class="sui-badge">이전 정산</span>
                    <span v-else-if="request.status === 'PAID'" class="sui-badge sui-badge--green">완료</span>
                    <span v-else class="sui-badge sui-badge--amber">신청</span>
                    <span v-if="request.paidAt" class="sui-table__sub">{{ formatDateTime(request.paidAt) }}</span>
                  </td>
                  <td class="sset__actions">
                    <button type="button" class="sui-btn sui-btn--sm" @click="toggleBundle(request.id)">
                      {{ opened === request.id ? '접기' : '건별 보기' }}
                    </button>
                    <button type="button" class="sui-btn sui-btn--sm" @click="onExcel(request)">엑셀</button>
                    <button
                      v-if="request.kind !== 'LEGACY' && request.status === 'REQUESTED'"
                      type="button"
                      class="sui-btn sui-btn--sm"
                      :disabled="busy"
                      @click="onCancel(request)"
                    >
                      신청 취소
                    </button>
                  </td>
                </tr>
                <tr v-if="opened === request.id" class="sset__bundle-row">
                  <td colspan="5">
                    <table class="sset__lines">
                      <thead>
                        <tr>
                          <th>주문번호</th>
                          <th class="is-num">판매대금</th>
                          <th class="is-num">수수료</th>
                          <th class="is-num">배송비</th>
                          <th class="is-num">지급액</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in request.items" :key="item.id">
                          <td>
                            <span class="sui-table__strong">{{ item.orderCode }}</span>
                            <span class="sui-table__sub">
                              {{ item.productName }}
                              <template v-if="item.optionLabel"> · {{ item.optionLabel }}</template>
                              · {{ item.qty }}{{ item.unit || '개' }}
                            </span>
                          </td>
                          <td class="is-num">{{ formatWon(item.salesAmount) }}원</td>
                          <td class="is-num sset__minus">-{{ formatWon(item.platformFee) }}원</td>
                          <td class="is-num">{{ formatWon(item.shippingFee) }}원</td>
                          <td class="is-num">{{ formatWon(item.payout) }}원</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div v-else class="sui-empty">
          <AppIcon icon="mdi-cash-off" :size="30" />
          <p>
            {{
              loading
                ? '불러오는 중입니다…'
                : view === 'eligible'
                  ? '정산할 수 있는 구매확정 건이 없습니다.'
                  : '정산신청내역이 없습니다.'
            }}
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { confirmDialog } from '@/composables/useConfirmDialog'
import { useSellerAuth } from '@/composables/useSellerAuth'
import { formatWon } from '@/constants/sellerData'
import {
  cancelSellerSettlementRequest,
  createSellerSettlementRequest,
  downloadSellerSettlementRequestExcel,
  fetchSellerSettlementBoard,
  type AdminSettlementItem,
  type SettlementRequest,
} from '@/api/mallOrder'

const { policy } = useSellerAuth()

const view = ref<'eligible' | 'requests'>('eligible')
const eligible = ref<AdminSettlementItem[]>([])
const requests = ref<SettlementRequest[]>([])
const availableCount = ref(0)
const availableSum = ref(0)
const requestedCount = ref(0)
const requestedSum = ref(0)
const paidCount = ref(0)
const paidSum = ref(0)
const period = ref('')
const keyword = ref('')
const loading = ref(false)
const busy = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const opened = ref(0)
const selected = reactive(new Set<number>())

function formatPeriodLabel(value: string) {
  const [year, month] = value.split('-')
  if (!year || !month) return value
  return `${year}년 ${Number(month)}월`
}

const periodOptions = computed(() => {
  const options: { value: string; label: string }[] = []
  const now = new Date()
  for (let i = 0; i < 18; i += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    options.push({ value, label: `${formatPeriodLabel(value)} 구매확정분` })
  }
  return options
})

const hasAccount = computed(() => Boolean(policy.value.settleBank && policy.value.settleAccount))

const payoutAccount = computed(() => {
  const { settleBank, settleAccount } = policy.value
  if (!settleBank || !settleAccount) return '미등록'
  const masked = `${settleAccount.slice(0, 4)}${'*'.repeat(Math.max(0, settleAccount.length - 8))}${settleAccount.slice(-4)}`
  return `${settleBank} ${masked}`
})

const selectedRows = computed(() => eligible.value.filter((row) => selected.has(row.itemIdx)))
const selectedCount = computed(() => selectedRows.value.length)
const selectedPayout = computed(() =>
  selectedRows.value.reduce((sum, row) => sum + Number(row.payout || 0), 0),
)
const allSelected = computed(
  () => eligible.value.length > 0 && eligible.value.every((row) => selected.has(row.itemIdx)),
)
const someSelected = computed(() => eligible.value.some((row) => selected.has(row.itemIdx)))

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—'
  const text = String(value).replace('T', ' ')
  return text.length >= 16 ? text.slice(0, 16) : text
}

function toggle(itemIdx: number) {
  if (selected.has(itemIdx)) selected.delete(itemIdx)
  else selected.add(itemIdx)
}

function toggleAll(checked: boolean) {
  if (checked) eligible.value.forEach((row) => selected.add(row.itemIdx))
  else eligible.value.forEach((row) => selected.delete(row.itemIdx))
}

function toggleBundle(id: number) {
  opened.value = opened.value === id ? 0 : id
}

function clearSelection() {
  selected.clear()
}

function pruneSelection() {
  const alive = new Set(eligible.value.map((row) => row.itemIdx))
  ;[...selected].forEach((idx) => {
    if (!alive.has(idx)) selected.delete(idx)
  })
}

async function reload() {
  loading.value = true
  errorMessage.value = ''
  successMessage.value = ''
  try {
    const result = await fetchSellerSettlementBoard({
      period: period.value || undefined,
      keyword: keyword.value || undefined,
    })
    eligible.value = result.eligible || []
    requests.value = result.requests || []
    availableCount.value = Number(result.availableCount || 0)
    availableSum.value = Number(result.availableSum || 0)
    requestedCount.value = Number(result.requestedCount || 0)
    requestedSum.value = Number(result.requestedSum || 0)
    paidCount.value = Number(result.paidCount || 0)
    paidSum.value = Number(result.paidSum || 0)
    pruneSelection()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '정산 내역을 불러오지 못했습니다.'
    eligible.value = []
    requests.value = []
  } finally {
    loading.value = false
  }
}

async function onRequest() {
  if (busy.value || !selectedCount.value) return
  if (!hasAccount.value) {
    errorMessage.value = '정산 계좌를 먼저 등록해 주세요.'
    return
  }
  const ok = await confirmDialog({
    title: '정산 신청',
    message: `선택한 ${selectedCount.value}건을 하나의 정산 신청으로 묶을까요?`,
    detail: `지급액 ${formatWon(selectedPayout.value)}원\n신청 후 정산신청내역에서 묶음으로 확인할 수 있습니다.`,
    confirmLabel: '정산 신청',
    tone: 'primary',
  })
  if (!ok) return
  busy.value = true
  errorMessage.value = ''
  try {
    const result = await createSellerSettlementRequest(selectedRows.value.map((row) => row.itemIdx))
    clearSelection()
    view.value = 'requests'
    await reload()
    successMessage.value = result.message
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '정산 신청에 실패했습니다.'
  } finally {
    busy.value = false
  }
}

async function onCancel(request: SettlementRequest) {
  const ok = await confirmDialog({
    title: '신청 취소',
    message: '이 정산 신청을 취소할까요? 포함된 건은 다시 정산 가능 목록으로 돌아갑니다.',
    detail: request.title,
    confirmLabel: '신청 취소',
    tone: 'danger',
  })
  if (!ok) return
  busy.value = true
  try {
    await cancelSellerSettlementRequest(request.id)
    await reload()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '요청 취소에 실패했습니다.'
  } finally {
    busy.value = false
  }
}

async function onExcel(request: SettlementRequest) {
  try {
    await downloadSellerSettlementRequestExcel(request)
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '엑셀을 내려받지 못했습니다.'
  }
}

onMounted(reload)

const steps = [
  {
    title: '구매확정된 건만',
    desc: '배송이 끝난 뒤 고객이 구매확정하거나, 배송완료 7일이 지나면 정산 가능 목록에 올라옵니다.',
  },
  {
    title: '건을 골라 신청',
    desc: '하나씩 고르거나, 기간·검색으로 좁힌 뒤 검색 결과 전체를 선택해 한 묶음으로 신청합니다.',
  },
  {
    title: '관리자가 완료',
    desc: '정산신청내역에서 묶음과 포함 건을 확인합니다. 관리자가 정산하고 완료를 누르면 그 신청은 끝납니다.',
  },
]
</script>

<style lang="scss" src="./SellerSettlementsPage.scss"></style>
